import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Mail, MessageSquare, Phone, Users, Pencil, Trash2, Star, StickyNote, Globe, MessagesSquare } from 'lucide-react';
import api from '../../../utils/api';
import { useConfirm } from '../../../components/ConfirmDialog';
import { Panel, Button, Pill, RowMenu, Empty, LoadingBlock, ErrorBlock, useLoad, errMsg, fmtDate, words } from '../kit';
import CommTimeModal from '../workflow/CommTimeModal';
import { CommModal, SurveyModal } from './modals';
import { COMM_CHANNEL, COMM_DIR, daysFromToday, inText } from './shared';

// Communications tab: the email, SMS, call and meeting log. Any entry can have its time logged,
// which then counts in SC hours and claims like a note.
const CHANNEL_ICON = { email: Mail, sms: MessageSquare, call: Phone, meeting: Users, note: StickyNote, portal: Globe };

export default function CommsTab({ detail, participantName, refreshSignal }) {
  const id = detail.id;
  const comms = useLoad(`/support-coordination/${id}/communications`);
  const surveys = useLoad(`/support-coordination/${id}/surveys`);
  const { reload: reloadComms } = comms;
  const { reload: reloadSurveys } = surveys;
  useEffect(() => { if (refreshSignal) { reloadComms(); reloadSurveys(); } }, [refreshSignal, reloadComms, reloadSurveys]);
  const confirm = useConfirm();
  const [modal, setModal] = useState(null);       // { editing? } | { send: 'email' | 'sms', to, name }
  const [survey, setSurvey] = useState(null);
  const [commTime, setCommTime] = useState(null);
  const rows = useMemo(() => [...(comms.data || [])].sort((a, b) => String(b.comm_date || b.created_at || '').localeCompare(String(a.comm_date || a.created_at || ''))), [comms.data]);

  const remove = async (c) => {
    if (!await confirm('Delete this communication? It leaves this file and is kept in the records archive for the retention period.', { title: 'Delete communication', danger: true, confirmLabel: 'Delete' })) return;
    try { await api.delete(`/support-coordination/communications/${c.id}`); toast.success('Communication deleted'); reloadComms(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const surveyTo = () => ({ channel: detail.client_email ? 'email' : 'sms', to: detail.client_email || detail.client_phone || '' });

  return (
    <div className="space-y-5">
      <Panel icon={MessagesSquare} title="Communications"
        sub="Emails, SMS, calls and meetings with or about this participant. Log time on any of them to count it in SC hours and claims.">
        <div className="flex flex-wrap gap-2 mb-4">
          <Button variant="secondary" size="sm" icon={Phone} onClick={() => setModal({})}>Log a call or meeting</Button>
          <Button variant="secondary" size="sm" icon={Mail} onClick={() => setModal({ send: 'email', to: detail.client_email || '', name: participantName })}>Send email</Button>
          <Button variant="secondary" size="sm" icon={MessageSquare} onClick={() => setModal({ send: 'sms', to: detail.client_phone || '', name: participantName })}>Send SMS</Button>
          <Button variant="ghost" size="sm" icon={Star} onClick={() => setSurvey(surveyTo())}>Ask for feedback</Button>
        </div>
        {comms.error ? <ErrorBlock error={comms.error} onRetry={reloadComms} />
          : !comms.data ? <LoadingBlock rows={4} />
          : !rows.length ? (
            <Empty icon={MessagesSquare} title="Nothing logged yet">
              Send an email or SMS from here, or log a call or meeting. Each one is kept on this file.
            </Empty>
          ) : (
            <ul className="divide-y divide-slate-100 border-t border-slate-100">
              {rows.map((c) => <CommRow key={c.id} c={c} onLogTime={() => setCommTime(c)} onEdit={() => setModal({ editing: c })} onDelete={() => remove(c)} />)}
            </ul>
          )}
      </Panel>

      {(surveys.data || []).length > 0 && <Feedback surveys={surveys.data} />}

      {modal && <CommModal scId={id} modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); reloadComms(); }} />}
      {survey && <SurveyModal scId={id} modal={survey} onClose={() => setSurvey(null)} onSent={() => { setSurvey(null); reloadSurveys(); reloadComms(); }} />}
      <CommTimeModal comm={commTime} onClose={() => setCommTime(null)} onSaved={() => { setCommTime(null); reloadComms(); }} />
    </div>
  );
}

function CommRow({ c, onLogTime, onEdit, onDelete }) {
  const Icon = CHANNEL_ICON[c.channel] || MessagesSquare;
  const when = c.comm_date || c.created_at;
  const sendable = c.channel === 'email' || c.channel === 'sms';
  const follow = daysFromToday(c.follow_up_date);
  const contactHref = !c.contact_to ? null : (c.channel === 'call' || c.channel === 'sms') ? `tel:${c.contact_to}` : c.channel === 'email' ? `mailto:${c.contact_to}` : null;
  const timeLogged = !!(c.time_entry_id || c.case_note_id);
  return (
    <li className="py-3.5 flex items-start gap-3">
      <span className="mt-0.5 w-8 h-8 rounded-xl bg-violet-50 text-violet-700 flex items-center justify-center flex-shrink-0"><Icon size={15} aria-hidden /></span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
          <div className="min-w-0 flex-1 basis-56">
            <p className="text-[13.5px] font-semibold text-slate-900 break-words">{c.subject || (c.body ? String(c.body).split('\n')[0].slice(0, 120) : COMM_CHANNEL[c.channel] || words(c.channel))}</p>
            <p className="text-[12px] text-slate-500 mt-0.5">
              {[COMM_CHANNEL[c.channel] || words(c.channel), COMM_DIR[c.direction] || (c.direction ? words(c.direction) : null), fmtDate(when)].filter(Boolean).join(' · ')}
              {(c.contact_name || c.contact_to) && <> · {c.contact_name}{c.contact_name && c.contact_to ? ' ' : ''}
                {c.contact_to && (contactHref
                  ? <a href={contactHref} className="text-indigo-700 hover:underline">{c.contact_name ? `(${c.contact_to})` : c.contact_to}</a>
                  : <span>{c.contact_name ? `(${c.contact_to})` : c.contact_to}</span>)}</>}
            </p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {timeLogged ? <Pill tone="violet" size="xs" label="Time logged" />
              : <Button variant="soft" size="sm" onClick={onLogTime}>Log time</Button>}
            <RowMenu label="Communication actions" items={[
              { label: 'Edit', icon: Pencil, onClick: onEdit },
              { label: 'Delete', icon: Trash2, danger: true, onClick: onDelete },
            ]} />
          </div>
        </div>
        {c.subject && c.body && <p className="mt-1 text-[13px] text-slate-700 line-clamp-2 whitespace-pre-line">{c.body}</p>}
        {(sendable || c.follow_up_date) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {sendable && (c.sent ? <Pill tone="emerald" size="xs" label="Sent" />
              : c.send_error ? <span title={c.send_error}><Pill tone="amber" size="xs" label="Not sent" /></span> : null)}
            {c.send_error && !c.sent && <span className="text-[12px] text-amber-900">{c.send_error}</span>}
            {c.follow_up_date && (
              <span className={clsx('text-[12px]', follow != null && follow < 0 ? 'font-semibold text-rose-700' : follow != null && follow <= 7 ? 'font-semibold text-amber-800' : 'text-slate-600')}>
                Follow up {fmtDate(c.follow_up_date)}{follow != null && follow <= 7 ? `, ${inText(follow)}` : ''}
              </span>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function Feedback({ surveys }) {
  const responded = surveys.filter((s) => s.status === 'responded');
  return (
    <Panel icon={Star} hue="amber" title="Feedback" sub={`${responded.length} of ${surveys.length} answered`}>
      <ul className="divide-y divide-slate-100">
        {surveys.map((s) => (
          <li key={s.id} className="py-2.5 flex items-start justify-between gap-3">
            <div className="min-w-0">
              {s.status === 'responded' ? (
                <span className="inline-flex items-center gap-0.5" role="img" aria-label={`Rated ${s.rating} out of 5`}>
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={14} fill={s.rating >= n ? '#f59e0b' : 'none'} color={s.rating >= n ? '#f59e0b' : '#94a3b8'} strokeWidth={1.5} aria-hidden />)}
                </span>
              ) : <Pill tone="slate" size="xs" label={`Asked by ${s.channel === 'sms' ? 'SMS' : 'email'}, no answer yet`} />}
              {s.comment && <p className="text-[13px] text-slate-700 mt-1">“{s.comment}”</p>}
            </div>
            <span className="text-[12px] text-slate-500 whitespace-nowrap">{fmtDate(s.responded_at || s.sent_at)}</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
